import api from './api';
import { TimeEntry } from '../types/api';
import { API_BASE_URL } from '../config';

// Get all users for filter dropdown
export const getUsers = async (): Promise<{id: number, name: string}[]> => {
  const response = await api.get<any[]>('/users');
  return response.data.map(user => ({
    id: user.id,
    name: user.username || user.email  // Use username since first_name/last_name don't exist yet
  }));
};

// Get all projects for filter dropdown
export const getProjects = async (): Promise<{id: number, name: string}[]> => {
  const response = await api.get<any[]>('/projects');
  return response.data.map(project => ({
    id: project.id,
    name: project.name
  }));
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const getEntries = async (filters?: {
  project_id?: number;
  user_id?: number;
  start_date?: string;
  end_date?: string;
}): Promise<TimeEntry[]> => {
  const params = new URLSearchParams();
  if (filters?.project_id) params.append('project_id', filters.project_id.toString());
  if (filters?.user_id) params.append('user_id', filters.user_id.toString());
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);
  
  const response = await api.get<TimeEntry[]>(`/time-entries?${params.toString()}`);
  return response.data;
};

export const getEntry = async (id: number): Promise<TimeEntry> => {
  const response = await api.get<TimeEntry>(`/time-entries/${id}`);
  return response.data;
};

export const createEntry = async (data: Omit<TimeEntry, 'id'>): Promise<TimeEntry> => {
  const response = await api.post<TimeEntry>('/time-entries', data);
  return response.data;
};

export const updateEntry = async (id: number, data: Partial<TimeEntry>): Promise<TimeEntry> => {
  const response = await api.put<TimeEntry>(`/time-entries/${id}`, data);
  return response.data;
};

export const deleteEntry = async (id: number): Promise<void> => {
  await api.delete(`/time-entries/${id}`);
};

// PDF Export function with Corporate Design
export const exportTimeEntriesToPDF = async (
  entries: TimeEntry[],
  filters?: {
    project_id?: number;
    user_id?: number;
    start_date?: string;
    end_date?: string;
  }
): Promise<void> => {
  // Dynamic import to avoid SSR issues
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;
  
  const doc = new jsPDF();
  
  // 🎨 PROFESSIONAL DESIGN SYSTEM
  const colors = {
    primary: [44, 94, 63] as [number, number, number],      // #2c5e3f - Rooftop Green
    primaryDark: [26, 71, 42] as [number, number, number],  // #1a472a - Dark Green
    secondary: [240, 240, 240] as [number, number, number], // #f0f0f0 - Light Gray
    white: [255, 255, 255] as [number, number, number],     // Pure White
    text: [51, 51, 51] as [number, number, number],         // #333333 - Dark Text
    accent: [74, 144, 226] as [number, number, number],     // #4a90e2 - Blue Accent
  };

  // 📄 MODERN HEADER DESIGN
  // Clean white background
  doc.setFillColor(...colors.white);
  doc.rect(0, 0, 210, 297, 'F');

  // Elegant header bar with gradient effect
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, 210, 45, 'F');

  // Logo integration with professional spacing
  try {
    const logoUrls = [
      `${window.location.origin}/api/static/logo.png`,
      `${window.location.origin}/static/logo.png`
    ];
    
    let logoLoaded = false;
    for (const logoUrl of logoUrls) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = logoUrl;
        });
        
        // Professional logo sizing
        const aspectRatio = logoImg.width / logoImg.height;
        const logoHeight = 35;
        const logoWidth = logoHeight * aspectRatio;
        
        // Create completely white version of logo
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = logoImg.width;
        canvas.height = logoImg.height;
        
        // Draw logo first
        ctx!.drawImage(logoImg, 0, 0);
        
        // Get image data and make it white
        const imageData = ctx!.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3]; // Get alpha channel
          if (alpha > 0) { // If pixel is not transparent
            data[i] = 255;     // Red = white
            data[i + 1] = 255; // Green = white  
            data[i + 2] = 255; // Blue = white
            // Keep original alpha
          }
        }
        
        ctx!.putImageData(imageData, 0, 0);
        const whiteLogoDataUrl = canvas.toDataURL();
        doc.addImage(whiteLogoDataUrl, 'PNG', 15, 5, logoWidth, logoHeight);
        logoLoaded = true;
        break;
      } catch (urlError) {
        continue;
      }
    }
    
    if (!logoLoaded) {
      // Elegant fallback
      doc.setTextColor(...colors.white);
      doc.setFont('Helvetica-Bold');
      doc.setFontSize(16);
      doc.text('ROOFTOP', 15, 25);
    }
  } catch (error) {
    console.warn('Logo loading failed:', error);
  }

  // Modern title and date in header
  doc.setTextColor(...colors.white);
  doc.setFont('Helvetica-Bold');
  doc.setFontSize(20);
  doc.text('Zeiterfassung Report', 75, 22);
  
  doc.setFont('Helvetica');
  doc.setFontSize(11);
  const currentDate = new Date().toLocaleDateString('de-DE');
  doc.text(`Erstellt: ${currentDate}`, 75, 35);

  // Company info aligned right
  doc.setFont('Helvetica');
  doc.setFontSize(10);
  doc.text('Rooftop Bedachungen GmbH', 150, 22);
  doc.text('Zeiterfassung & Controlling', 150, 32);
  
  // 📊 ELEGANT FILTER SECTION
  let yPos = 55;
  
  if (filters && Object.values(filters).some(v => v)) {
    // Filter section header with subtle background
    doc.setFillColor(...colors.secondary);
    doc.rect(15, yPos, 180, 20, 'F');
    
    doc.setTextColor(...colors.text);
    doc.setFont('Helvetica-Bold');
    doc.setFontSize(12);
    doc.text('Angewendete Filter', 20, yPos + 12);
    
    yPos += 25;
    
    // Filter items with clean layout
    doc.setFont('Helvetica');
    doc.setFontSize(10);
    
    if (filters.project_id) {
      const project = entries[0]?.project_name || 'Unbekannt';
      doc.setFillColor(...colors.white);
      doc.rect(20, yPos, 170, 8, 'F');
      doc.text(`Projekt: ${project}`, 25, yPos + 5);
      yPos += 10;
    }

    if (filters.user_id) {
      const user = entries[0]?.user_name || 'Unbekannt';
      doc.setFillColor(...colors.white);
      doc.rect(20, yPos, 170, 8, 'F');
      doc.text(`Mitarbeiter: ${user}`, 25, yPos + 5);
      yPos += 10;
    }

    if (filters.start_date || filters.end_date) {
      doc.setFillColor(...colors.white);
      doc.rect(20, yPos, 170, 8, 'F');
      const dateRange = `Zeitraum: ${filters.start_date ? new Date(filters.start_date).toLocaleDateString('de-DE') : 'Alle'} - ${filters.end_date ? new Date(filters.end_date).toLocaleDateString('de-DE') : 'Alle'}`;
      doc.text(dateRange, 25, yPos + 5);
      yPos += 10;
    }

    yPos += 10;
  } else {
    yPos += 5;
  }
  
  // Calculate total hours
  const totalMinutes = entries.reduce((sum, entry) => {
    const startTime = new Date(entry.start_time);
    const endTime = new Date(entry.end_time);
    const workMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60) - (entry.break_duration || 0);
    return sum + workMinutes;
  }, 0);
  
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = Math.round(totalMinutes % 60);
  
  // Table data
  const tableData = entries.map(entry => {
    const startTime = new Date(entry.start_time);
    const endTime = new Date(entry.end_time);
    const workMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60) - (entry.break_duration || 0);
    const workHours = Math.floor(workMinutes / 60);
    const workMins = Math.round(workMinutes % 60);
    
    return [
      new Date(entry.start_time).toLocaleDateString('de-DE'),
      entry.project_name || 'Unbekannt',
      entry.user_name || 'Unbekannt',
      startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      endTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      `${entry.break_duration || 0} min`,
      `${workHours}h ${workMins.toString().padStart(2, '0')}min`,
      entry.description || ''
    ];
  });
  
  // AutoTable for the time entries
    // 🗂️ PROFESSIONAL DATA TABLE
  autoTable(doc, {
    startY: yPos,
    head: [['Datum', 'Projekt', 'Mitarbeiter', 'Start', 'Ende', 'Pause', 'Arbeitszeit', 'Beschreibung']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: colors.primary,
      textColor: colors.white,
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      lineWidth: 0.5,
      lineColor: colors.primaryDark
    },
    bodyStyles: {
      fontSize: 9,
      textColor: colors.text,
      lineWidth: 0.3,
      lineColor: [200, 200, 200],
      cellPadding: 2
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },  // Datum
      1: { cellWidth: 38, halign: 'left' },    // Projekt  
      2: { cellWidth: 32, halign: 'left' },    // Mitarbeiter
      3: { cellWidth: 14, halign: 'center' },  // Start
      4: { cellWidth: 14, halign: 'center' },  // Ende
      5: { cellWidth: 15, halign: 'center' },  // Pause
      6: { cellWidth: 24, halign: 'center', fontStyle: 'bold' }, // Arbeitszeit
      7: { cellWidth: 28, halign: 'left' }     // Beschreibung
    },
    margin: { left: 13, right: 14 },
    styles: {
      overflow: 'linebreak',
      cellWidth: 'wrap'
    }
  });
  
  // 📈 ELEGANT SUMMARY SECTION
  const finalY = (doc as any).lastAutoTable.finalY + 15;
  
  // Summary box with modern design - horizontal centered
  const boxWidth = 170;
  const boxX = (210 - boxWidth) / 2; // Center horizontally (perfect centering)
  
  doc.setFillColor(...colors.primary);
  doc.roundedRect(boxX, finalY, boxWidth, 25, 3, 3, 'F');
  
  // White border for elegance
  doc.setDrawColor(...colors.white);
  doc.setLineWidth(1);
  doc.roundedRect(boxX, finalY, boxWidth, 25, 3, 3, 'S');
  
  // Summary content - perfectly centered vertically and horizontally
  doc.setTextColor(...colors.white);
  doc.setFont('Helvetica-Bold');
  doc.setFontSize(14);
  
  // Center "Gesamtarbeitszeit:" text (higher up for better centering)
  const gesamtText = 'Gesamtarbeitszeit:';
  const gesamtWidth = doc.getTextWidth(gesamtText);
  doc.text(gesamtText, boxX + (boxWidth - gesamtWidth) / 2, finalY + 10);
  
  // Center total hours text (higher up for better centering)
  doc.setFont('Helvetica-Bold');
  doc.setFontSize(16);
  const totalText = `${totalHours}h ${remainingMinutes.toString().padStart(2, '0')}min`;
  const totalWidth = doc.getTextWidth(totalText);
  doc.text(totalText, boxX + (boxWidth - totalWidth) / 2, finalY + 20);
  
  // 🎯 PROFESSIONAL FOOTER
  const pageHeight = doc.internal.pageSize.height;
  
  // Footer line
  doc.setDrawColor(...colors.secondary);
  doc.setLineWidth(0.5);
  doc.line(15, pageHeight - 25, 195, pageHeight - 25);
  
  // Footer content with modern layout
  doc.setTextColor(...colors.text);
  doc.setFont('Helvetica');
  doc.setFontSize(8);
  doc.text('Rooftop Bedachungen GmbH', 15, pageHeight - 15);
  doc.text('Zeiterfassung & Controlling System', 15, pageHeight - 8);
  
  // Page number and generation info
  doc.text('Seite 1', 180, pageHeight - 15);
  doc.text(`Generiert: ${new Date().toLocaleString('de-DE')}`, 120, pageHeight - 8);
  
  // Download
  const fileName = `Zeiterfassung_${currentDate.replace(/\./g, '-')}.pdf`;
  doc.save(fileName);
};

export const exportTimeEntries = async (filters?: {
  project_id?: number;
  start_date?: string;
  end_date?: string;
}): Promise<void> => {
  const queryParams = new URLSearchParams();
  if (filters?.project_id) {
    queryParams.append('project_id', filters.project_id.toString());
  }
  if (filters?.start_date) {
    queryParams.append('start_date', filters.start_date);
  }
  if (filters?.end_date) {
    queryParams.append('end_date', filters.end_date);
  }

  const response = await fetch(`${API_BASE_URL}/time-entries/export?${queryParams}`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw new Error('Failed to export time entries');
  }

  // Create a blob from the response and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'time_entries.csv';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getTimeEntryHistory = async (id: number): Promise<Array<{
  id: number;
  user_id: number;
  change_type: 'create' | 'update' | 'delete';
  old_values: any;
  new_values: any;
  created_at: string;
}>> => {
  const response = await fetch(`${API_BASE_URL}/time-entries/${id}/history`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    throw new Error('Failed to fetch time entry history');
  }
  return response.json();
}; 